import { useCallback, useEffect, useMemo, useState } from "react";
import { readStoredUser } from "../../utils/sessionUser";
import { hasPurchasingSettingsPermission } from "../../utils/purchasingSettingsPermissions";

// Keep each loaded settings form tied to the user, hotel and permissions that opened it.
export function usePurchasingSettingsSection(scope) {
  const [, refreshSession] = useState(0);
  const user = readStoredUser();
  const userId = user?.id;
  const businessUnitId = Number(user?.active_business_unit_id) || null;
  const canView = hasPurchasingSettingsPermission(user, scope, "view");
  const canEdit = canView && hasPurchasingSettingsPermission(user, scope, "update");
  const context = JSON.stringify([userId, businessUnitId, canView, canEdit]);
  useEffect(() => {
    const refresh = () => refreshSession((value) => value + 1);
    window.addEventListener("palantir-session-updated", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("palantir-session-updated", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);
  const isCurrent = useCallback((action = "view") => {
    const current = readStoredUser();
    return current?.id === userId && (Number(current?.active_business_unit_id) || null) === businessUnitId
      && hasPurchasingSettingsPermission(current, scope, "view")
      && hasPurchasingSettingsPermission(current, scope, action);
  }, [businessUnitId, scope, userId]);
  const requestConfig = useMemo(() => ({
    pilcoreExpectedUserId: userId,
    headers: { "X-Pilcore-Business-Unit-Id": businessUnitId },
  }), [businessUnitId, userId]);
  return { user, businessUnitId, canView, canEdit, context, isCurrent, requestConfig };
}
